import React from 'react';
import { useShow, IResourceComponentsProps } from '@refinedev/core';
import { Show, DateField } from '@refinedev/antd';
import { Typography, Tag, Descriptions, Card, Progress, Row, Col } from 'antd';
import { PlayerData } from '@/types/index';

const { Title } = Typography;

const PLAYER_TYPES = ['Batsman', 'Bowler', 'All-Rounder'];
const PLAYER_TYPE_COLORS = ['blue', 'red', 'purple'];
const BOWLER_TYPES: Record<number, string> = {
  [-1]: 'Non-Bowler',
  0: 'Fast',
  1: 'Off Break',
  2: 'Leg Break',
  3: 'Medium',
};
const BATTING_HAND = ['Right', 'Left'];
const BOWLING_ACTION = ['Normal', 'Side-arm', 'Round-arm'];
const BASE_STAT_ROLES = ['Batter', 'Bowler', 'All-Rounder', 'Wicket Keeper'];

interface StatBarProps {
  label: string;
  value: number | null;
  max: number;
}

const StatBar: React.FC<StatBarProps> = ({ label, value, max }) => {
  if (value === null || value === undefined) {
    return (
      <div style={{ marginBottom: 8 }}>
        <span style={{ display: 'inline-block', width: 120 }}>{label}</span>
        <Tag>N/A</Tag>
      </div>
    );
  }
  const percent = Math.round((value / max) * 100);
  return (
    <div style={{ marginBottom: 8 }}>
      <span style={{ display: 'inline-block', width: 120 }}>{label}</span>
      <Progress
        percent={percent}
        size="small"
        style={{ width: 200, display: 'inline-block' }}
        format={() => `${value}/${max}`}
      />
    </div>
  );
};

export const PlayerDataShow: React.FC<IResourceComponentsProps> = () => {
  const { queryResult } = useShow();
  const { data, isLoading } = queryResult;
  const record = data?.data as PlayerData | undefined;

  return (
    <Show isLoading={isLoading}>
      <Title level={5}>Player Identity</Title>
      <Descriptions bordered column={2} size="small">
        <Descriptions.Item label="Player Index">{record?.playerIndex}</Descriptions.Item>
        <Descriptions.Item label="Display Name">{record?.displayName}</Descriptions.Item>
        <Descriptions.Item label="First Name">{record?.firstName}</Descriptions.Item>
        <Descriptions.Item label="Second Name">{record?.secondName}</Descriptions.Item>
        <Descriptions.Item label="Team">
          {record?.teamName || '-'} (Index: {record?.teamIndex})
        </Descriptions.Item>
        <Descriptions.Item label="Nationality">{record?.nationality || '-'}</Descriptions.Item>
        <Descriptions.Item label="Player Type">
          <Tag color={PLAYER_TYPE_COLORS[record?.defaultPlayerType ?? 0]}>
            {PLAYER_TYPES[record?.defaultPlayerType ?? 0]}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Bowler Type">
          {BOWLER_TYPES[record?.defaultBowlerType ?? -1] ?? 'Unknown'}
        </Descriptions.Item>
        <Descriptions.Item label="Batting Hand">
          {BATTING_HAND[record?.battingHand ?? 0]}
        </Descriptions.Item>
        <Descriptions.Item label="Bowling Hand">
          {BATTING_HAND[record?.bowlingHand ?? 0]}
        </Descriptions.Item>
        <Descriptions.Item label="Bowling Action">
          {BOWLING_ACTION[record?.bowlingAction ?? 0]}
        </Descriptions.Item>
        <Descriptions.Item label="Wicket Keeper">
          {record?.isWicketKeeper ? <Tag color="cyan">Yes</Tag> : 'No'}
        </Descriptions.Item>
        <Descriptions.Item label="Jersey Number">{record?.jerseyNumber}</Descriptions.Item>
        <Descriptions.Item label="Role">{record?.role || 'Player'}</Descriptions.Item>
        <Descriptions.Item label="Face Asset ID">{record?.faceAssetId}</Descriptions.Item>
        <Descriptions.Item label="Status">
          <Tag color={record?.isActive ? 'green' : 'red'}>
            {record?.isActive ? 'Active' : 'Inactive'}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Created">
          <DateField value={record?.createdAt} />
        </Descriptions.Item>
        <Descriptions.Item label="Updated">
          <DateField value={record?.updatedAt} />
        </Descriptions.Item>
      </Descriptions>

      {record?.baseStats ? (
        <Card
          title={`Base Stats (${BASE_STAT_ROLES[record.baseStats.role] || 'Unknown'})`}
          style={{ marginTop: 20 }}
          size="small"
        >
          <Row gutter={32}>
            <Col xs={24} md={8}>
              <Title level={5} style={{ fontSize: 14 }}>
                Physical (PHY)
              </Title>
              <StatBar label="Speed" value={record.baseStats.phySpeed} max={10} />
              <StatBar label="Stamina" value={record.baseStats.phyStamina} max={10} />
              <StatBar label="Power" value={record.baseStats.phyPower} max={10} />
              <StatBar label="Reflex" value={record.baseStats.phyReflex} max={10} />
              <StatBar label="Acrobatics" value={record.baseStats.phyAcrobatics} max={10} />
            </Col>
            <Col xs={24} md={8}>
              <Title level={5} style={{ fontSize: 14 }}>
                Skill (SKL)
              </Title>
              <StatBar label="Footwork" value={record.baseStats.sklFootwork} max={15} />
              <StatBar label="Timing" value={record.baseStats.sklTiming} max={20} />
              <StatBar label="Range" value={record.baseStats.sklRange} max={10} />
              <StatBar label="Bowling" value={record.baseStats.sklBowling} max={10} />
              <StatBar label="WK" value={record.baseStats.sklWk} max={15} />
            </Col>
            <Col xs={24} md={8}>
              <Title level={5} style={{ fontSize: 14 }}>
                Accuracy (ACC) & Composure
              </Title>
              <StatBar label="Bat Accuracy" value={record.baseStats.accBat} max={20} />
              <StatBar label="Bowl Accuracy" value={record.baseStats.accBowl} max={20} />
              <StatBar label="Field Accuracy" value={record.baseStats.accField} max={20} />
              <StatBar label="Composure" value={record.baseStats.cmp} max={10} />
            </Col>
          </Row>
        </Card>
      ) : (
        <Card style={{ marginTop: 20 }} size="small">
          <Title level={5} style={{ color: '#999' }}>
            No base stats defined for this player
          </Title>
        </Card>
      )}
    </Show>
  );
};
